import React, { CSSProperties } from "react";

import { PickerView } from "./picker_view";
import { IconName } from "../components";

export interface PickerOption<TValue extends string | number = any> {
  label: string;
  value: TValue;
  disabled?: boolean;
}

interface PickerProps<TValue extends string | number = any> {
  testID?: string;
  value?: TValue;
  onChange: (value: TValue) => void;
  options: PickerOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  name?: string;
  title?: string | null;
  description?: string | null;
  view?: React.ReactNode;
  icon?: IconName;
  override?: {
    root?: { style?: CSSProperties };
    view?: { style?: CSSProperties };
    arrow?: { style?: CSSProperties };
  };
}

export function Picker<TValue extends string | number = any>(
  props: PickerProps<TValue>
) {
  const {
    placeholder,
    options,
    value,
    onChange,
    disabled,
    description,
    view,
    name,
    title,
    icon,
    testID,
    invalid,
    override,
  } = props;

  const selected = options.find((opt) => opt.value === value);
  const isNumeric = options.every((o) => typeof o.value === "number");

  return (
    <div style={override?.root?.style} className="picker">
      {view || (
        <PickerView
          placeholder={placeholder}
          title={title || selected?.label}
          description={description}
          disabled={disabled}
          icon={icon}
          invalid={invalid}
          override={{ root: override?.view, arrow: override?.arrow }}
        />
      )}
      <select
        name={name}
        disabled={disabled}
        className="picker-select"
        data-testid={testID}
        value={value || ""}
        onChange={(event) => {
          if (isNumeric) {
            onChange(Number(event.currentTarget.value) as TValue);
          } else {
            onChange(event.currentTarget.value as TValue);
          }
        }}
      >
        {placeholder && (
          <option disabled value="">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            disabled={option.disabled}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
